import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  LinearProgress,
  TablePagination,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  InputAdornment
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  LockReset as ResetIcon,
  FileUpload as ImportIcon,
  FileDownload as DownloadIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  CompareArrows as CompareArrowsIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';

const SupervisorManager = () => {
  const [supervisors, setSupervisors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string>('ALL');
  const [filterRole, setFilterRole] = useState<string>('ALL');
  const [filterAssignment, setFilterAssignment] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importMessage, setImportMessage] = useState({ type: 'info', text: '' });
  const [selected, setSelected] = useState<string[]>([]);

  // Major Mapping State
  const [majorMappingOpen, setMajorMappingOpen] = useState(false);
  const [majorMappings, setMajorMappings] = useState<any[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [newMappingDept, setNewMappingDept] = useState('');
  const [customDeptText, setCustomDeptText] = useState('');
  const [newMappingMajor, setNewMappingMajor] = useState('');
  const [customMajorText, setCustomMajorText] = useState('');
  const [newMappingSupervisorId, setNewMappingSupervisorId] = useState('');
  const [newMappingNotes, setNewMappingNotes] = useState('');
  const [mappingSearch, setMappingSearch] = useState('');
  const [availableDepartmentOptions, setAvailableDepartmentOptions] = useState<string[]>([]);
  const [availableStudentMajors, setAvailableStudentMajors] = useState<string[]>([]);
  const [databaseStudentMajors, setDatabaseStudentMajors] = useState<string[]>([]);

  const fetchStudentMajors = async () => {
    try {
      const res = await api.get('/supervisors/student-majors');
      if (res.data?.studentMajors) setDatabaseStudentMajors(res.data.studentMajors);
    } catch (e) {
      console.error('Failed to fetch student majors:', e);
    }
  };

  const fetchMappingOptions = async () => {
    try {
      const res = await api.get('/supervisors/mapping-options');
      if (res.data?.departments) setAvailableDepartmentOptions(res.data.departments);
      if (res.data?.majors) setAvailableStudentMajors(res.data.majors);
    } catch (e) {
      console.error('Failed to fetch mapping options:', e);
    }
  };

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    prefix: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    department: '',
    is_professional: true,
    is_specialist: true
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchSupervisors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/supervisors');
      setSupervisors(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMajorMappings = async () => {
    setLoadingMappings(true);
    try {
      const response = await api.get('/supervisors/major-mappings');
      setMajorMappings(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleOpenMajorMapping = () => {
    setMajorMappingOpen(true);
    fetchMajorMappings();
    fetchMappingOptions();
  };

  const handleCreateMapping = async () => {
    const deptToSave = newMappingDept === '__CUSTOM__' ? customDeptText.trim() : newMappingDept.trim();
    const majorToSave = newMappingMajor === '__CUSTOM__' ? customMajorText.trim() : newMappingMajor.trim();

    if (!deptToSave || !majorToSave) {
      alert('กรุณาเลือกหรือระบุสังกัด/สาขาวิชาอาจารย์ และเลือกสาขาวิชานักศึกษา');
      return;
    }
    setLoadingMappings(true);
    try {
      await api.post('/supervisors/major-mappings', {
        department_name: deptToSave,
        student_major: majorToSave,
        supervisor_id: newMappingSupervisorId || null,
        notes: newMappingNotes.trim() || null
      });
      setNewMappingDept('');
      setCustomDeptText('');
      setNewMappingMajor('');
      setCustomMajorText('');
      setNewMappingSupervisorId('');
      setNewMappingNotes('');
      await fetchMajorMappings();
      await fetchMappingOptions();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกการจับคู่');
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleDeleteMapping = async (id: string) => {
    if (window.confirm('ยืนยันลบการจับคู่สาขาวิชานี้?')) {
      setLoadingMappings(true);
      try {
        await api.delete(`/supervisors/major-mappings/${id}`);
        await fetchMajorMappings();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบการจับคู่');
      } finally {
        setLoadingMappings(false);
      }
    }
  };

  const handleSeedDefaultMappings = async () => {
    if (window.confirm('ต้องการสร้างรายการจับคู่สาขาวิชามาตรฐานอัตโนมัติหรือไม่? (เช่น คณิตศาสตร์ประยุกต์ -> คณิตศาสตร์, วิทยาศาสตร์ศึกษา -> ฟิสิกส์/เคมี/ชีววิทยา, อุตสาหกรรมศิลป์ -> การศึกษา (อุตสาหกรรมศิลป์))')) {
      setLoadingMappings(true);
      try {
        const res = await api.post('/supervisors/major-mappings/seed-defaults');
        alert(res.data.message || 'สร้างข้อมูลมาตรฐานสำเร็จ');
        await fetchMajorMappings();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการสร้างข้อมูลมาตรฐาน');
      } finally {
        setLoadingMappings(false);
      }
    }
  };

  useEffect(() => {
    fetchSupervisors();
    fetchMappingOptions();
    fetchStudentMajors();
  }, []);

  const handleResetPassword = async (userId: string, username: string) => {
    const newPassword = window.prompt(`ระบุรหัสผ่านใหม่สำหรับอาจารย์: ${username}`);
    if (newPassword) {
      setLoading(true);
      try {
        await api.post('/auth/admin-reset-password', { userId, newPassword });
        alert('รีเซ็ตรหัสผ่านเรียบร้อยแล้ว');
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  // สังกัด/สาขาวิชาของอาจารย์นิเทศก์ที่มีอยู่จริงในระบบเท่านั้น
  const availableDepartments = React.useMemo(() => {
    const set = new Set<string>();
    if (availableDepartmentOptions.length > 0) {
      availableDepartmentOptions.forEach(d => {
        if (d && d.trim()) set.add(d.trim());
      });
    }
    supervisors.forEach((s: any) => {
      if (s.department && s.department.trim()) set.add(s.department.trim());
    });
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'th'));
  }, [supervisors, availableDepartmentOptions]);

  // สาขาวิชาของนักศึกษาที่มีอยู่จริงในระบบเท่านั้น
  const studentMajorsList = React.useMemo(() => {
    const set = new Set<string>();
    if (availableStudentMajors.length > 0) {
      availableStudentMajors.forEach(m => {
        if (m && m.trim()) set.add(m.trim());
      });
    }
    if (databaseStudentMajors.length > 0) {
      databaseStudentMajors.forEach(m => {
        if (m && m.trim()) set.add(m.trim());
      });
    }
    if (set.size === 0) {
      DEFAULT_STUDENT_MAJORS.forEach(m => set.add(m));
    }
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'th'));
  }, [availableStudentMajors, databaseStudentMajors]);

  // ตัวเลือกสาขาวิชาสำหรับฟอร์มเพิ่ม/แก้ไขอาจารย์นิเทศก์
  const supervisorFormDepartments = React.useMemo(() => {
    const set = new Set<string>();
    availableDepartments.forEach(d => set.add(d));
    studentMajorsList.forEach(m => set.add(m));
    if (formData.department && formData.department.trim()) {
      set.add(formData.department.trim());
    }
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'th'));
  }, [availableDepartments, studentMajorsList, formData.department]);

  const totalSupervisorsCount = supervisors.length;
  const profCount = supervisors.filter((s: any) => !!s.is_professional).length;
  const specCount = supervisors.filter((s: any) => !!s.is_specialist).length;
  const assignedCount = supervisors.filter((s: any) => Number(s.assigned_student_count || 0) > 0).length;

  const filteredSupervisors = supervisors.filter((s: any) => {
    if (filterDepartment !== 'ALL' && s.department !== filterDepartment) {
      return false;
    }
    if (filterRole === 'PROFESSIONAL' && !s.is_professional) {
      return false;
    }
    if (filterRole === 'SPECIALIST' && !s.is_specialist) {
      return false;
    }
    if (filterRole === 'BOTH' && (!s.is_professional || !s.is_specialist)) {
      return false;
    }
    if (filterAssignment === 'ASSIGNED' && Number(s.assigned_student_count || 0) === 0) {
      return false;
    }
    if (filterAssignment === 'UNASSIGNED' && Number(s.assigned_student_count || 0) > 0) {
      return false;
    }
    if (filterStatus === 'ACTIVE' && (!s.is_active || s.is_active === 0)) {
      return false;
    }
    if (filterStatus === 'INACTIVE' && (s.is_active === 1 || s.is_active === true)) {
      return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchSearch =
        s.first_name?.toLowerCase().includes(term) ||
        s.last_name?.toLowerCase().includes(term) ||
        s.prefix?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term) ||
        s.department?.toLowerCase().includes(term) ||
        s.username?.toLowerCase().includes(term) ||
        s.phone_number?.includes(term);
      if (!matchSearch) return false;
    }
    return true;
  });

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterDepartment, filterRole, filterAssignment, filterStatus]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredSupervisors.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`ยืนยันการย้ายอาจารย์นิเทศก์ที่เลือก ${selected.length} รายการ ไปยังถังขยะ?`)) {
      setLoading(true);
      try {
        await api.post('/supervisors/bulk-soft-delete', { ids: selected });
        setSelected([]);
        fetchSupervisors();
        alert('ย้ายข้อมูลไปยังถังขยะเรียบร้อยแล้ว');
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบข้อมูล');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportExcel = () => {
    const exportData = filteredSupervisors.map((s: any) => ({
      'Username': s.username,
      'คำนำหน้า': s.prefix || '',
      'ชื่อ': s.first_name,
      'นามสกุล': s.last_name,
      'Email': s.email,
      'เบอร์โทรศัพท์': s.phone_number,
      'สาขาวิชา': s.department,
      'ด้านวิชาชีพครู': s.is_professional ? 'ใช่' : 'ไม่ใช่',
      'ด้านวิชาเอก': s.is_specialist ? 'ใช่' : 'ไม่ใช่'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Supervisors');
    XLSX.writeFile(workbook, `tpms_supervisors_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleOpen = (supervisor: any = null) => {
    if (supervisor) {
      setEditingId(supervisor.id);
      setFormData({
        username: supervisor.username || supervisor.email || '',
        password: '',
        prefix: supervisor.prefix || '',
        first_name: supervisor.first_name,
        last_name: supervisor.last_name,
        email: supervisor.email || '',
        phone_number: supervisor.phone_number || '',
        department: supervisor.department,
        is_professional: !!supervisor.is_professional,
        is_specialist: !!supervisor.is_specialist
      });
    } else {
      setEditingId(null);
      setFormData({ 
        username: '', 
        password: '', 
        prefix: '',
        first_name: '', 
        last_name: '', 
        email: '',
        phone_number: '',
        department: '', 
        is_professional: true, 
        is_specialist: true 
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    
    if (name === 'email') {
        setFormData(prev => ({
            ...prev,
            email: finalValue,
            username: (prev.username === '' || prev.username === prev.email) ? finalValue : prev.username
        }));
    } else {
        setFormData({ ...formData, [name]: finalValue });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dataToSend = {
          ...formData,
          username: formData.username || formData.email
      };

      if (editingId) {
        await api.put(`/supervisors/${editingId}`, dataToSend);
      } else {
        await api.post('/supervisors', dataToSend);
      }
      handleClose();
      fetchSupervisors();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบอาจารย์นิเทศก์? รายการจะถูกย้ายไปยังถังขยะ')) {
      setLoading(true);
      try {
        await api.delete(`/supervisors/${id}`);
        fetchSupervisors();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileUpload = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedSupervisors = results.data.map((row: any) => ({
          username: row['Username'] || row['username'] || row['Email'] || row['email'],
          password: row['Password'] || row['password'] || row['Username'] || row['username'],
          prefix: row['คำนำหน้า'] || row['prefix'],
          first_name: row['ชื่อ'] || row['first_name'],
          last_name: row['นามสกุล'] || row['last_name'],
          email: row['Email'] || row['email'],
          phone_number: row['เบอร์โทร'] || row['phone_number'],
          department: row['สาขาวิชา'] || row['department'],
          is_professional: (row['ด้านวิชาชีพ'] || row['is_professional'])?.toString().toLowerCase() === 'true' || (row['ด้านวิชาชีพ'] || row['is_professional']) === '1',
          is_specialist: (row['ด้านวิชาเอก'] || row['is_specialist'])?.toString().toLowerCase() === 'true' || (row['ด้านวิชาเอก'] || row['is_specialist']) === '1'
        }));

        try {
          const res = await api.post('/supervisors/bulk', { supervisors: parsedSupervisors });
          setImportMessage({ type: 'success', text: `นำเข้าข้อมูลสำเร็จ ${res.data.results.length} ท่าน` });
          fetchSupervisors();
        } catch (err: any) {
          setImportMessage({ type: 'error', text: err.response?.data?.message || 'การนำเข้าข้อมูลล้มเหลว' });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleDownloadTemplate = () => {
    const headers = ['Username', 'Password', 'คำนำหน้า', 'ชื่อ', 'นามสกุล', 'Email', 'เบอร์โทร', 'สาขาวิชา', 'ด้านวิชาชีพ', 'ด้านวิชาเอก'];
    const sampleRow = ['supervisor01', 'pass1234', 'ผศ.ดร.', 'สมชาย', 'ใจดี', 'somchai@gmail.com', '0812345678', 'ภาษาไทย', '1', '1'];

    const csvContent = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'supervisor_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          จัดการข้อมูลอาจารย์นิเทศก์ (Supervisor Management)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <Button variant="outlined" color="primary" startIcon={<CompareArrowsIcon />} onClick={handleOpenMajorMapping} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
            จับคู่สาขาวิชา
          </Button>
          <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel}>
            ส่งออก Excel
          </Button>
          <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)}>
            นำเข้า CSV
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleOpen()} sx={{ whiteSpace: 'nowrap' }}>
            เพิ่มอาจารย์นิเทศก์ใหม่
          </Button>
        </Box>
      </Box>

      {/* Metric KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper 
            onClick={() => { setFilterRole('ALL'); setFilterAssignment('ALL'); }}
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              cursor: 'pointer',
              borderRadius: 2,
              border: filterRole === 'ALL' && filterAssignment === 'ALL' ? '2px solid #1E40AF' : '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              อาจารย์นิเทศก์ทั้งหมด
            </Typography>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800, mt: 0.5 }}>
              {totalSupervisorsCount} <Typography component="span" variant="body2" color="text.secondary">ท่าน</Typography>
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper 
            onClick={() => setFilterRole('PROFESSIONAL')}
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              cursor: 'pointer',
              borderRadius: 2,
              border: filterRole === 'PROFESSIONAL' ? '2px solid #2563EB' : '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              นิเทศก์สายวิชาชีพครู
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563EB', mt: 0.5 }}>
              {profCount} <Typography component="span" variant="body2" color="text.secondary">ท่าน</Typography>
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper 
            onClick={() => setFilterRole('SPECIALIST')}
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              cursor: 'pointer',
              borderRadius: 2,
              border: filterRole === 'SPECIALIST' ? '2px solid #9333EA' : '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              นิเทศก์เฉพาะสาขาวิชา
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#9333EA', mt: 0.5 }}>
              {specCount} <Typography component="span" variant="body2" color="text.secondary">ท่าน</Typography>
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper 
            onClick={() => setFilterAssignment('ASSIGNED')}
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              cursor: 'pointer',
              borderRadius: 2,
              border: filterAssignment === 'ASSIGNED' ? '2px solid #059669' : '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              มีนักศึกษาในความดูแลแล้ว
            </Typography>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 800, mt: 0.5 }}>
              {assignedCount} <Typography component="span" variant="body2" color="text.secondary">ท่าน</Typography>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Comprehensive Filter Toolbar */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          {/* Department / Major Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sup-dept-label">สาขาวิชา</InputLabel>
              <Select
                labelId="sup-dept-label"
                value={filterDepartment}
                label="สาขาวิชา"
                onChange={(e) => setFilterDepartment(e.target.value)}
              >
                <MenuItem value="ALL">ทุกสาขาวิชา (ทั้งหมด)</MenuItem>
                {availableDepartments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Supervision Role Filter */}
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sup-role-label">บทบาทการนิเทศ</InputLabel>
              <Select
                labelId="sup-role-label"
                value={filterRole}
                label="บทบาทการนิเทศ"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="ALL">ทุกบทบาท (ทั้งหมด)</MenuItem>
                <MenuItem value="PROFESSIONAL">วิชาชีพครู (Professional)</MenuItem>
                <MenuItem value="SPECIALIST">เฉพาะสาขาวิชา (Specialist)</MenuItem>
                <MenuItem value="BOTH">ทั้งสองด้าน (Both)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Assignment Status Filter */}
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sup-assign-label">สถานะการมอบหมาย</InputLabel>
              <Select
                labelId="sup-assign-label"
                value={filterAssignment}
                label="สถานะการมอบหมาย"
                onChange={(e) => setFilterAssignment(e.target.value)}
              >
                <MenuItem value="ALL">ทุกสถานะการมอบหมาย</MenuItem>
                <MenuItem value="ASSIGNED">มีนักศึกษาในความดูแลแล้ว</MenuItem>
                <MenuItem value="UNASSIGNED">ยังไม่มีนักศึกษา</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Account Status Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sup-status-label">สถานะบัญชี</InputLabel>
              <Select
                labelId="sup-status-label"
                value={filterStatus}
                label="สถานะบัญชี"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">ทุกสถานะบัญชี</MenuItem>
                <MenuItem value="ACTIVE">ปกติ (Active)</MenuItem>
                <MenuItem value="INACTIVE">ระงับ (Inactive)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Search Bar */}
          <Grid size={{ xs: 12 }}>
            <TextField 
              size="small"
              placeholder="ค้นหาชื่อ-สกุล, อีเมล, username, เบอร์โทรศัพท์ หรือสาขาวิชา..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', boxShadow: 2, borderRadius: 2 }}>
        {selected.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'error.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
              เลือกอาจารย์นิเทศก์แล้ว {selected.length} รายการ
            </Typography>
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<DeleteIcon />} 
              onClick={handleBulkDelete}
              size="small"
            >
              ลบรายการที่เลือก
            </Button>
          </Box>
        )}
        <Table sx={{ minWidth: 950 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredSupervisors.length}
                  checked={filteredSupervisors.length > 0 && selected.length === filteredSupervisors.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ-นามสกุล</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>อีเมล (Google)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>เบอร์โทรศัพท์</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ประเภท</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>นศ. ในความดูแล</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>สถานะบัญชี</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSupervisors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((s: any) => {
              const isItemSelected = selected.indexOf(s.id) !== -1;
              return (
                <TableRow 
                  key={s.id} 
                  hover 
                  selected={isItemSelected}
                  onClick={() => handleSelectOne(s.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{s.prefix || ''}{s.first_name} {s.last_name}</TableCell>
                  <TableCell>{s.email || '-'}</TableCell>
                  <TableCell>{s.phone_number || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {s.is_professional ? <Chip label="วิชาชีพ" size="small" color="primary" sx={{ height: 22, fontSize: '0.72rem' }} /> : null}
                      {s.is_specialist ? <Chip label="วิชาเอก" size="small" color="secondary" sx={{ height: 22, fontSize: '0.72rem' }} /> : null}
                    </Box>
                  </TableCell>
                  <TableCell>{s.department}</TableCell>
                  <TableCell align="center">
                    {Number(s.assigned_student_count || 0) > 0 ? (
                      <Chip 
                        label={`${s.assigned_student_count} คน`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">0 คน</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {s.is_active ? (
                      <Chip label="ปกติ" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ) : (
                      <Chip label="ระงับ" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell>{s.username}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="รีเซ็ตรหัสผ่าน">
                        <IconButton color="warning" onClick={() => handleResetPassword(s.user_id, s.username)} size="small">
                          <ResetIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="แก้ไข">
                        <IconButton color="primary" onClick={() => handleOpen(s)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ลบ">
                        <IconButton color="error" onClick={() => handleDelete(s.id)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredSupervisors.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  ไม่พบข้อมูลอาจารย์นิเทศก์ตามเงื่อนไขที่เลือก
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredSupervisors.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Import CSV Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImportIcon color="primary" /> นำเข้าข้อมูลอาจารย์นิเทศก์จากไฟล์ CSV
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'primary.main' }}>
              <HelpIcon fontSize="small" /> รายละเอียดรูปแบบไฟล์ (CSV Format):
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลพื้นฐาน (จำเป็น):</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ชื่อ, นามสกุล:</b> ชื่อภาษาไทย</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>Email:</b> สำหรับ Login Google (Gmail)</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>Username:</b> สำหรับล็อกอินปกติ (ถ้าเว้นว่างจะใช้อีเมล)</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>ข้อมูลเฉพาะด้าน:</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>ด้านวิชาชีพ / ด้านวิชาเอก:</b> ใส่ 1 หรือ 0</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>สาขาวิชา:</b> เช่น การประถมศึกษา, ภาษาไทย</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>เบอร์โทร:</b> ตัวเลข 10 หลัก</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>• <b>คำนำหน้า:</b> เช่น นาย, นาง, ผศ.ดร.</Typography>
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                    size="small" 
                    variant="contained" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                >
                    ดาวน์โหลดไฟล์เทมเพลตอาจารย์นิเทศก์
                </Button>
            </Box>
          </Box>

          {importMessage.text && <Alert severity={importMessage.type as any} sx={{ mb: 2 }}>{importMessage.text}</Alert>}

          <Box sx={{ border: '2px dashed #ccc', p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'grey.50' }}>
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              onChange={handleFileUpload}
            />
            <label htmlFor="csv-file-input">
              <Button variant="outlined" component="span" startIcon={<ImportIcon />} size="large">
                เลือกไฟล์ CSV จากเครื่องคอมพิวเตอร์
              </Button>
            </label>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
              คำแนะนำ: ไฟล์ต้องมีการเข้ารหัสเป็น UTF-8 เพื่อรองรับภาษาไทย
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            {editingId ? 'แก้ไขข้อมูลอาจารย์นิเทศก์' : 'เพิ่มข้อมูลอาจารย์นิเทศก์'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Username (ชื่อผู้ใช้)"
                name="username"
                value={formData.username}
                onChange={handleChange}
                helperText="หากเว้นว่าง ระบบจะใช้อีเมลเป็น Username"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                type="password"
                label="Password (รหัสผ่าน)"
                name="password"
                value={formData.password}
                onChange={handleChange}
                helperText={editingId ? "เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน" : "กำหนดรหัสผ่านสำหรับเข้าใช้งาน"}
                required={!editingId}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="อีเมลสำหรับ Login Google (Gmail เท่านั้น)"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@gmail.com"
                required
                helperText="สำคัญ: ใช้ตรวจสอบสิทธิ์การ Login ด้วย Google"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="คำนำหน้าชื่อ"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                placeholder="เช่น ผศ.ดร., อาจารย์, นาย, นาง"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="ชื่อ"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="นามสกุล"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>ประเภทอาจารย์นิเทศก์ (เลือกได้มากกว่า 1)</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={formData.is_professional} onChange={handleChange} name="is_professional" />}
                  label="ด้านวิชาชีพครู"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.is_specialist} onChange={handleChange} name="is_specialist" />}
                  label="ด้านวิชาเอก"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="สาขาวิชา/สังกัด"
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
                helperText="รายการสาขาวิชาดึงจากข้อมูลนักศึกษาที่มีอยู่จริงในระบบ และสังกัดของอาจารย์"
              >
                <MenuItem value=""><em>-- เลือกสาขาวิชา/สังกัด --</em></MenuItem>
                {supervisorFormDepartments.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} {databaseStudentMajors.includes(m) ? ' (สาขาวิชานักศึกษาในระบบ)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Major Mapping Dialog */}
      <Dialog 
        open={majorMappingOpen} 
        onClose={() => setMajorMappingOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              จัดการจับคู่สาขาวิชา/สังกัด (Major & Department Mapping)
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.875rem' }}>
            ฟังก์ชันนี้ใช้สำหรับจับคู่สังกัดของอาจารย์นิเทศก์กับสาขาวิชาของนักศึกษา เพื่อแก้ไขปัญหาชื่อสาขาวิชาไม่ตรงกัน (เช่น คำว่า คู่ขนาน, วงเล็บ, ค.บ.) หรือกรณีอาจารย์นิเทศก์ได้รับมอบหมายให้ดูแลนักศึกษาข้ามสาขาวิชา ระบบจะนำข้อมูลนี้ไปจับคู่แสดงรายชื่อนักศึกษาในหน้าการนิเทศและหน้ากรอกคะแนนภาระงาน
          </Alert>

          {/* Add Mapping Box */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1e293b' }}>
              + เพิ่มการจับคู่สาขาวิชาใหม่
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="map-dept-label">สังกัด/สาขาวิชาอาจารย์นิเทศก์ *</InputLabel>
                  <Select
                    labelId="map-dept-label"
                    value={newMappingDept}
                    label="สังกัด/สาขาวิชาอาจารย์นิเทศก์ *"
                    onChange={(e) => setNewMappingDept(e.target.value)}
                  >
                    {availableDepartments.map((dept) => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                    <MenuItem value="__CUSTOM__">
                      <em>-- ระบุสังกัด/สาขาอื่นด้วยตนเอง --</em>
                    </MenuItem>
                  </Select>
                </FormControl>
                {newMappingDept === '__CUSTOM__' && (
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                    label="พิมพ์ระบุสังกัด/สาขาวิชาของอาจารย์ *"
                    value={customDeptText}
                    onChange={(e) => setCustomDeptText(e.target.value)}
                    placeholder="เช่น คณิตศาสตร์ประยุกต์"
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="map-major-label">สาขาวิชาของนักศึกษา *</InputLabel>
                  <Select
                    labelId="map-major-label"
                    value={newMappingMajor}
                    label="สาขาวิชาของนักศึกษา *"
                    onChange={(e) => setNewMappingMajor(e.target.value)}
                  >
                    {studentMajorsList.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                    <MenuItem value="__CUSTOM__">
                      <em>-- ระบุสาขาวิชาอื่นด้วยตนเอง --</em>
                    </MenuItem>
                  </Select>
                </FormControl>
                {newMappingMajor === '__CUSTOM__' && (
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                    label="พิมพ์ระบุสาขาวิชาของนักศึกษา *"
                    value={customMajorText}
                    onChange={(e) => setCustomMajorText(e.target.value)}
                    placeholder="เช่น คณิตศาสตร์, การศึกษา (อุตสาหกรรมศิลป์)"
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="map-sup-label">ขอบเขตอาจารย์นิเทศก์ (เว้นว่าง = ทุกท่านในสังกัดนี้)</InputLabel>
                  <Select
                    labelId="map-sup-label"
                    value={newMappingSupervisorId}
                    label="ขอบเขตอาจารย์นิเทศก์ (เว้นว่าง = ทุกท่านในสังกัดนี้)"
                    onChange={(e) => setNewMappingSupervisorId(e.target.value)}
                  >
                    <MenuItem value=""><em>มีผลกับอาจารย์ทุกคนในสังกัดนี้</em></MenuItem>
                    {supervisors.map((s: any) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.prefix || ''}{s.first_name} {s.last_name} ({s.department || 'ไม่ระบุสังกัด'})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="หมายเหตุ (เช่น หลักสูตรคู่ขนาน, มอบหมายข้ามสาขา)"
                  value={newMappingNotes}
                  onChange={(e) => setNewMappingNotes(e.target.value)}
                  placeholder="เช่น หลักสูตรคู่ขนาน"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleSeedDefaultMappings}
                    disabled={loadingMappings}
                  >
                    สร้างค่าเริ่มต้นอัตโนมัติ (Seed Defaults)
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleCreateMapping}
                    disabled={
                      loadingMappings ||
                      !newMappingDept ||
                      (newMappingDept === '__CUSTOM__' && !customDeptText.trim()) ||
                      !newMappingMajor ||
                      (newMappingMajor === '__CUSTOM__' && !customMajorText.trim())
                    }
                  >
                    เพิ่มการจับคู่
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Existing Mappings Header & Search */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              รายการจับคู่สาขาวิชาปัจจุบัน ({majorMappings.length} รายการ)
            </Typography>
            <TextField
              size="small"
              placeholder="ค้นหาสาขา, สังกัด หรืออาจารย์..."
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{ width: { xs: '100%', sm: 260 } }}
            />
          </Box>

          {/* Mappings Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>สังกัดอาจารย์นิเทศก์</TableCell>
                  <TableCell align="center" sx={{ width: 40, bgcolor: '#f1f5f9' }}></TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>สาขาวิชานักศึกษา</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>ขอบเขต</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>หมายเหตุ</TableCell>
                  <TableCell align="center" sx={{ width: 60, fontWeight: 700, bgcolor: '#f1f5f9' }}>ลบ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {majorMappings
                  .filter((m: any) => {
                    if (!mappingSearch.trim()) return true;
                    const q = mappingSearch.toLowerCase();
                    const dept = (m.department_name || '').toLowerCase();
                    const major = (m.student_major || '').toLowerCase();
                    const notes = (m.notes || '').toLowerCase();
                    const supName = `${m.sup_prefix || ''}${m.sup_first_name || ''} ${m.sup_last_name || ''}`.toLowerCase();
                    return dept.includes(q) || major.includes(q) || notes.includes(q) || supName.includes(q);
                  })
                  .map((m: any) => (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: '#1e3a8a' }}>
                        {m.department_name}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', px: 0.5 }}>
                        <CompareArrowsIcon fontSize="small" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#047857' }}>
                        {m.student_major}
                      </TableCell>
                      <TableCell>
                        {m.supervisor_id ? (
                          <Chip 
                            size="small" 
                            label={`${m.sup_prefix || ''}${m.sup_first_name || ''} ${m.sup_last_name || ''}`}
                            color="secondary" 
                            variant="outlined"
                          />
                        ) : (
                          <Chip 
                            size="small" 
                            label="อาจารย์ทุกคนในสังกัด" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {m.notes || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleDeleteMapping(m.id)}
                          title="ลบรายการจับคู่"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {majorMappings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      ยังไม่มีรายการจับคู่สาขาวิชา (ท่านสามารถกดปุ่ม "สร้างค่าเริ่มต้นอัตโนมัติ" เพื่อสร้างรายการมาตรฐานได้)
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMajorMappingOpen(false)} variant="contained">
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupervisorManager;
